import React, {useEffect, useMemo, useState} from 'react'
import {Box, Button, Stack, Typography} from '@mui/material'
import moment from 'moment'
import SidebarPanelContainerUi from '@ui/sidebarPanel/sidebarPanelContainerUi/SidebarPanelContainerUi'
import SidebarPanelUi from '@ui/sidebarPanel/SidebarPanelUi'
import {useAppContext} from '@context/appContext/hooks/useAppContext'
import {useQueryState} from '@hooks/query/useQueryState'
import {useMedia} from '@hooks/ui/useMedia'
import {
	useGetPassengerChartRouteQuery,
	useGetPassengerChartRoutesQuery
} from '@containers/monitoringPlatform/passengerCharts/queryHooks/usePassengerChartsQuery'
import PassengerChartsSidebar from '@containers/monitoringPlatform/passengerCharts/sidebar/PassengerChartsSidebar'
import {DATE_FORMAT_PASSENGER_CHARTS} from '@containers/monitoringPlatform/passengerCharts/constants'
import {routeNumber} from '@containers/monitoringPlatform/passengerCharts/helpers/routeNumber'
import {formatRange} from '@containers/monitoringPlatform/passengerCharts/helpers/formatTime'
import {buildSegments, circlesOf, ISegment} from '@containers/monitoringPlatform/passengerVerification/helpers/buildSegments'
import SegmentsTable from '@containers/monitoringPlatform/passengerVerification/segments/SegmentsTable'
import SegmentPhotosPanel from '@containers/monitoringPlatform/passengerVerification/photos/SegmentPhotosPanel'
import sx from '@containers/monitoringPlatform/passengerVerification/sx'

const isDesktopWidth = () =>
	typeof window !== 'undefined' && typeof window.matchMedia === 'function'
		? window.matchMedia('(min-width:1200px)').matches
		: true

const yesterday = () => moment().subtract(1, 'day').format(DATE_FORMAT_PASSENGER_CHARTS)

const PassengerVerification = () => {
	const {account} = useAppContext()
	const {isDesktop} = useMedia()
	const [showSidebar, setShowSidebar] = useState(isDesktopWidth)
	const [date, setDate] = useQueryState<string>('pvDate', yesterday())
	const [routeId, setRouteId] = useQueryState<number | null>('pvRoute', null, {
		parse: (value) => Number(value) || null
	})
	const [vypuskIndex, setVypuskIndex] = useState(0)
	const [circle, setCircle] = useState<number | null>(null)
	const [segment, setSegment] = useState<ISegment | null>(null)

	const routesQuery = useGetPassengerChartRoutesQuery(account.id, date)
	const routeQuery = useGetPassengerChartRouteQuery(account.id, routeId, date)
	const route = routeId ? routeQuery.data : undefined
	const vypusk = route?.vypusky[vypuskIndex] ?? route?.vypusky[0]
	const circles = useMemo(() => (vypusk ? circlesOf(vypusk) : []), [vypusk])
	const activeCircle = circle != null && circles.includes(circle) ? circle : circles[0] ?? null
	const segments = useMemo(
		() => (vypusk && activeCircle != null ? buildSegments(vypusk, activeCircle) : []),
		[vypusk, activeCircle]
	)

	useEffect(() => {
		setVypuskIndex(0)
		setCircle(null)
		setSegment(null)
	}, [routeId, date])

	const onSelectRoute = (value: number | null) => {
		setRouteId(value)
		if (!isDesktop) {
			setShowSidebar(false)
		}
	}

	const onChangeDate = (value: string) => {
		setDate(value)
		setRouteId(null)
	}

	return (
		<SidebarPanelContainerUi isLoading={routesQuery.isFetching || routeQuery.isFetching}>
			<SidebarPanelUi
				showSidebar={showSidebar}
				setShowSidebar={setShowSidebar}
				buttonVariantToggleShow={'absolute'}
				mobileVariant={'drawer'}
				mobileToggleVariant={'fab'}
				desktopWidth={'260px'}
			>
				<PassengerChartsSidebar
					date={date}
					onChangeDate={onChangeDate}
					routes={routesQuery.data ?? []}
					routeId={routeId}
					onSelectRoute={onSelectRoute}
					isLoading={routesQuery.isLoading}
				/>
			</SidebarPanelUi>

			<Box sx={sx.main}>
				{!route && <Typography sx={sx.hint}>Оберіть маршрут, щоб звірити кількість пасажирів на міжзупинкових перегонах</Typography>}

				{route && vypusk && <>
					<Box sx={sx.header}>
						<Typography sx={sx.title}>Маршрут №{routeNumber(route.routeName)}</Typography>
						<Typography sx={sx.subtitle}>
							{Array.from(new Set(vypusk.vehicles.map((v) => v.licencePlate))).join(', ')}
							{vypusk.vehicles.length > 0 && ` · ${formatRange(vypusk.vehicles[0].from, vypusk.vehicles[vypusk.vehicles.length - 1].to)}`}
						</Typography>
					</Box>

					{route.vypusky.length > 1 && <Stack direction="row" gap="6px" flexWrap="wrap" mb={1}>
						{route.vypusky.map((v, i) => (
							<Button key={v.scheduleId} size="small" sx={sx.chip}
									variant={v === vypusk ? 'contained' : 'outlined'}
									onClick={() => {
										setVypuskIndex(i)
										setCircle(null)
										setSegment(null)
									}}>
								Випуск {v.number}
							</Button>
						))}
					</Stack>}

					<Stack direction="row" gap="6px" flexWrap="wrap" mb={1.5}>
						{circles.map((c) => (
							<Button key={c} size="small" sx={sx.chip}
									variant={c === activeCircle ? 'contained' : 'outlined'}
									onClick={() => {
										setCircle(c)
										setSegment(null)
									}}>
								Коло {c}
							</Button>
						))}
					</Stack>

					{!route.countsAvailable && <Typography sx={sx.hint}>Немає даних підрахунку</Typography>}

					<SegmentsTable segments={segments} activeKey={segment?.key ?? null} onSelect={setSegment}/>
				</>}
			</Box>

			<SegmentPhotosPanel segment={segment} onClose={() => setSegment(null)}/>
		</SidebarPanelContainerUi>
	)
}

export default PassengerVerification
